import React from "react"
import SectionCard from "../../../shared/components/SectionCard"
import { ui } from "../../../shared/styles/uiSystem"

function EmptyState({ loading, text }) {
  return (
    <div style={{ color: "#64748b", fontSize: 13 }}>
      {loading ? "Cargando..." : text}
    </div>
  )
}

function CollapseButton({ collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...ui.secondaryButton,
        minWidth: 42,
        minHeight: 36
      }}
    >
      {collapsed ? "+" : "−"}
    </button>
  )
}

function SummaryMetric({ label, value, helper }) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 4,
        alignContent: "start"
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 22,
          lineHeight: 1.05,
          fontWeight: 900,
          color: "#0f172a"
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            lineHeight: 1.35
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  )
}

function ForecastDayRow({
  label,
  projectedLabel,
  scheduledLabel,
  appointmentsLabel,
  percent
}) {
  return (
    <div
      style={{
        ...ui.softCard,
        padding: 12,
        display: "grid",
        gap: 8
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap"
        }}
      >
        <div style={{ display: "grid", gap: 3 }}>
          <div
            style={{
              fontWeight: 900,
              fontSize: 14,
              color: "#0f172a"
            }}
          >
            {label}
          </div>

          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              fontWeight: 700
            }}
          >
            {appointmentsLabel} · {scheduledLabel}
          </div>
        </div>

        <div
          style={{
            fontSize: 13,
            color: "#0f172a",
            fontWeight: 900,
            whiteSpace: "nowrap"
          }}
        >
          {projectedLabel}
        </div>
      </div>

      <div
        style={{
          height: 10,
          borderRadius: 999,
          background: "#e2e8f0",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: "linear-gradient(90deg, #2563eb, #14b8a6)",
            borderRadius: 999
          }}
        />
      </div>
    </div>
  )
}

export default function WeeklyForecastPanel({
  weeklyForecast,
  collapsed,
  onToggle,
  loading,
  isMobile,
  formatMoney
}) {
  const days = Array.isArray(weeklyForecast?.days) ? weeklyForecast.days : []

  const maxProjected = Math.max(
    1,
    ...days.map((row) => Number(row?.projectedRevenue || 0))
  )

  const totalProjected = Number(weeklyForecast?.totalProjectedRevenue || 0)
  const avgTicket = Number(weeklyForecast?.avgTicketPerAppointment || 0)
  const totalAppointments = days.reduce(
    (acc, row) => acc + Number(row?.appointments || 0),
    0
  )

  return (
    <SectionCard
      title="Pronóstico semanal"
      subtitle="Ingresos esperados por día"
      actions={
        <CollapseButton
          collapsed={collapsed}
          onClick={onToggle}
        />
      }
    >
      {!collapsed && (
        <div style={{ display: "grid", gap: 12 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
              gap: 10
            }}
          >
            <SummaryMetric
              label="Total proyectado"
              value={`$${formatMoney(totalProjected)}`}
              helper="Suma esperada de la semana"
            />

            <SummaryMetric
              label="Ticket promedio"
              value={`$${formatMoney(avgTicket)}`}
              helper="Ingreso estimado por cita"
            />

            <SummaryMetric
              label="Citas proyectadas"
              value={formatCount(totalAppointments)}
              helper="Citas consideradas en esta vista"
            />
          </div>

          {!!days.length && (
            <div
              style={{
                padding: 12,
                borderRadius: 14,
                background:
                  "linear-gradient(180deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.96) 100%)",
                border: "1px solid #dbeafe",
                color: "#1e293b",
                fontSize: 13,
                lineHeight: 1.45,
                fontWeight: 700
              }}
            >
              Día más fuerte:{" "}
              <span style={{ fontWeight: 900 }}>
                {getTopDayLabel(days, formatMoney)}
              </span>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {days.map((day) => {
              const projected = Number(day?.projectedRevenue || 0)
              const percent = Math.max(6, Math.min(100, (projected / maxProjected) * 100))

              return (
                <ForecastDayRow
                  key={day?.date || Math.random()}
                  label={formatDayLabel(day?.date)}
                  projectedLabel={`Proyectado $${formatMoney(projected)}`}
                  scheduledLabel={`Programado $${formatMoney(day?.scheduledRevenue || 0)}`}
                  appointmentsLabel={`${formatCount(day?.appointments || 0)} citas`}
                  percent={percent}
                />
              )
            })}

            {!days.length ? (
              <EmptyState loading={loading} text="Sin pronóstico semanal." />
            ) : null}
          </div>
        </div>
      )}
    </SectionCard>
  )
}

function formatCount(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(safe)
}

function formatDayLabel(value) {
  if (!value) return "-"

  const date = new Date(value)

  if (!Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat("es-MX", {
      weekday: "long",
      day: "2-digit",
      month: "2-digit"
    }).format(date)
  }

  return String(value)
}

function getTopDayLabel(days, formatMoney) {
  if (!days.length) return "-"

  const top = [...days].sort(
    (a, b) => Number(b?.projectedRevenue || 0) - Number(a?.projectedRevenue || 0)
  )[0]

  return `${formatDayLabel(top?.date)} · $${formatMoney(top?.projectedRevenue || 0)}`
}